import { mapActions, mapMutations, mapGetters } from 'vuex';

import HtmlRenderContainer from '@/components/content-management/html-render-container';

import Questions from './questions';
import AttemptsResults from '@/components/gaia/student/quiz/attempts-results';

import AttemptSummaryModal from '@/components/gaia/modals/quiz/attempt-summary-modal';
import NotAnsweredAllQuestionsModal from '@/components/gaia/modals/quiz/not-answered-all-questions-modal';
import AnswerConfirmationModal from '@/components/gaia/modals/quiz/answer-confirm';

import QuizMixin from '@/mixins/quiz';
import ValidateTopicConfigurationsMixin from '@/mixins/validateTopicConfigurations';

export default {
  props: {
    content: {
      type    : Object,
      default : () => ({})
    },
    title: {
      type    : String,
      default : ''
    }
  },
  mixins     : [QuizMixin, ValidateTopicConfigurationsMixin],
  components : {
    Questions,
    AttemptsResults,
    HtmlRenderContainer,
    AttemptSummaryModal,
    AnswerConfirmationModal,
    NotAnsweredAllQuestionsModal
  },
  data() {
    return {
      isConfirmModalOpen   : false,
      notAnsweredModalData : {
        isOpen    : false,
        questions : ''
      },
      isAttemptSummaryModalOpen       : false,
      selectedAttemptToPreviewSummary : {},
      averageResult                   : {
        grade      : 0,
        totalGrade : 0,
        percentage : ''
      }
    };
  },
  created() {
    this.initiateStudentAttemptsObject();
    if (this.isAverageAttempts(this.content.retryTypeId)) {
      this.finalResult();
    }
  },
  updated() {
    this.initiateStudentAttemptsObject();
    if (this.isAverageAttempts(this.content.retryTypeId)) {
      this.finalResult();
    }
  },
  computed: {
    ...mapGetters({
      isImpersonated: '_core/auth/isImpersonated'
    })
  },
  methods: {
    ...mapActions({
      actionFinishQuizAttempt  : 'plataforma/enrollment/actionFinishQuizAttempt',
      actionAnswerQuizQuestion : 'plataforma/enrollment/actionAnswerQuizQuestion'
    }),
    ...mapMutations({
      UPDATE_QUIZ_CONTENT                                    : 'plataforma/enrollment/UPDATE_QUIZ_CONTENT',
      SET_TOPIC_VIEWED_ON_MENU                               : 'plataforma/content/SET_TOPIC_VIEWED_ON_MENU',
      ADD_NEW_ATTEMPT_TO_QUIZ                                : 'plataforma/enrollment/ADD_NEW_ATTEMPT_TO_QUIZ',
      UPDATE_QUIZ_ATTEMPT_ANSWER                             : 'plataforma/enrollment/UPDATE_QUIZ_ATTEMPT_ANSWER',
      SET_TOPIC_VIEWED_ON_SELECTED                           : 'plataforma/enrollment/SET_TOPIC_VIEWED_ON_SELECTED',
      ADD_ATTEMPT_WHEN_QUIZ_EXPIRED_AND_STUDENT_NOT_ANSWERED : 'plataforma/enrollment/ADD_ATTEMPT_WHEN_QUIZ_EXPIRED_AND_STUDENT_NOT_ANSWERED'
    }),
    getExpiredIconColor() {
      if (!this.content.isBeforeDeadline && !this.isQuizFinished(this.content)) {
        return '#EF5350';
      } else if (this.isQuizFinished(this.content)) {
        return '#00A285';
      }
    },
    getSentDateTitle() {
      if (this.isQuizFinished(this.content)) {
        return this.$t('student.quiz.taskSendDateIndicator');
      } else if (!this.content.isBeforeDeadline) {
        if (this.content.isAcademicMainExpired) {
          return this.$t('student.quiz.enrollmentExpiredDeadlineIndicator');
        } else {
          return this.$t('student.quiz.expiredDeadlineIndicator');
        }
      }
    },
    getDeadlineDate() {
      return this.getDeadlineDateAcademicMainOrTopicDeadline(this.content.isAcademicMainExpired, this.content.academicMainDeadlineDate, this.content.deadlineAt);
    },
    initiateStudentAttemptsObject() {
      if (!this.isQuizFinished(this.content)) {
        if (this.hasStudentFinishedLastAttempt(this.content.attempts, this.content.questions, this.content.hasRetries && this.content.numberRetries, this.content)) {
          this.ADD_NEW_ATTEMPT_TO_QUIZ(this.content.topicId);
        } else if (this.isQuizExpired(this.content) && !this.hasStudentInitiatedAtLeastOneAttempt(this.content)) {
          this.ADD_ATTEMPT_WHEN_QUIZ_EXPIRED_AND_STUDENT_NOT_ANSWERED(this.content.topicId);
        }
      }
    },
    openNotAnsweredAllQuestionsModal() {
      this.notAnsweredModalData = {
        isOpen    : true,
        questions : this.returnQuestionsNotAnswered()
      };
    },
    openAttemptSummaryModal(attempt) {
      this.selectedAttemptToPreviewSummary = attempt;
      this.isAttemptSummaryModalOpen = true;
    },
    returnQuestionsNotAnswered() {
      const currentAttempt = this.findCurrentAttempt(this.content.attempts);
      const questionsNotAnswered = [];

      if (currentAttempt && currentAttempt.answers) {
        if (currentAttempt.answers.length > 0) {
          this.content.questions.forEach((question, questionIndex) => {
            const studentAnswer = this.findQuestionInCurrentAttemptAnswers(question.questionId, currentAttempt.answers);

            if (!studentAnswer) {
              questionsNotAnswered.push(questionIndex + 1);
            }
          });
        } else {
          this.content.questions.forEach((_, index) => {
            questionsNotAnswered.push(index + 1);
          });
        }
      }

      return questionsNotAnswered.join(', ');
    },
    getCurrentAttemptNumber(content) {
      return content.attempts.length > 0 && content.attempts[content.attempts.length - 1].attemptNumber;
    },
    sendQuestionAnswer({ questionId, optionId }) {
      if (!this.isImpersonated) {
        this.$nuxt.$loading.start();

        this.actionAnswerQuizQuestion({ enrollmentId: this.$route.params.id, topic: this.content, questionId, optionId })
          .then((response) => {
            this.UPDATE_QUIZ_ATTEMPT_ANSWER({ attemptsData: response.attempts, topicId: this.content.topicId });
          })
          .catch((err) => {
            this.$toast.error(err);
          })
          .finally(() => {
            this.$nuxt.$loading.finish();
          });
      }
    },
    sendQuizAnswers() {
      if (!this.isImpersonated) {
        this.$nuxt.$loading.start();
        const currentAttempt = this.findCurrentAttempt();

        this.actionFinishQuizAttempt({ enrollmentId: this.$route.params.id, topic: this.content, attemptId: currentAttempt.attemptId })
          .then((response) => {
            this.UPDATE_QUIZ_CONTENT({ content: response, topicId: this.content.topicId });

            this.SET_TOPIC_VIEWED_ON_MENU({ topicId: this.content.topicId, viewed: true });
            this.SET_TOPIC_VIEWED_ON_SELECTED({ topicId: this.content.topicId, viewed: true });

            if (!response.config.allAttemptsCompleted) {
              this.ADD_NEW_ATTEMPT_TO_QUIZ(this.content.topicId);
            }
          })
          .catch((err) => {
            this.$toast.error(err);
          })
          .finally(() => {
            this.isConfirmModalOpen = false;

            this.$nuxt.$loading.finish();
          });
      }
    },
    finalResult() {
      if (this.content.isGrade) {
        this.averageResult.grade = Number(this.content.grade);
        const totalValue = this.content.questions.reduce((a, b) => ({ grade: Number(a.grade) + Number(b.grade) })).grade;
        this.averageResult.totalGrade = Math.ceil(totalValue * 10 + Number.EPSILON) / 10;
      } else {
        this.averageResult.grade = this.content.attempts.reduce((a, b) => ({ hits: Number(a.hits) + Number(b.hits) })).hits / this.content.attempts.length;
        this.averageResult.totalGrade = this.content.questions.length;
      };

      this.averageResult.percentage = (this.averageResult.grade / this.averageResult.totalGrade).toFixed(5) * 100;
    }
  }
};
